/**
 * Minimal webcam capture for the filter try-photo. `<input capture>` only
 * opens a camera on mobile browsers — on desktop it falls back to the file
 * picker — so "take a photo" needs real getUserMedia. Portaled to <body>
 * (the step slider's transform breaks position:fixed descendants).
 */

import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { FaCamera, FaTimes, FaSpinner } from 'react-icons/fa'
import { useCopy } from '../regionCopy'

interface CameraCaptureModalProps {
  onCapture: (dataUrl: string) => void
  onClose: () => void
  /** Called when the camera can't start — caller falls back to file picker. */
  onUnavailable: () => void
}

export const CameraCaptureModal: React.FC<CameraCaptureModalProps> = ({
  onCapture,
  onClose,
  onUnavailable
}) => {
  const c = useCopy()
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let cancelled = false
    void (async (): Promise<void> => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 1280 } },
          audio: false
        })
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        streamRef.current = stream
        if (videoRef.current) {
          videoRef.current.srcObject = stream
          await videoRef.current.play()
        }
        setReady(true)
      } catch {
        if (!cancelled) onUnavailable()
      }
    })()
    return () => {
      cancelled = true
      streamRef.current?.getTracks().forEach((t) => t.stop())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const snap = (): void => {
    const video = videoRef.current
    if (!video || !ready) return
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    // Mirror to match the on-screen selfie preview.
    ctx.translate(canvas.width, 0)
    ctx.scale(-1, 1)
    ctx.drawImage(video, 0, 0)
    onCapture(canvas.toDataURL('image/jpeg', 0.92))
    onClose()
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
      <div className="flex w-full max-w-md flex-col overflow-hidden rounded-3xl border border-white/10 bg-[#101014] shadow-2xl">
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
          <span className="flex items-center gap-2 text-sm font-semibold text-whiteout">
            <FaCamera className="text-blueSparkle" /> Test photo
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label={c.cancel}
            className="text-whiteout/50 hover:text-whiteout"
          >
            <FaTimes />
          </button>
        </div>

        <div className="relative aspect-[4/3] w-full bg-black">
          <video
            ref={videoRef}
            playsInline
            muted
            className="h-full w-full -scale-x-100 object-cover"
          />
          {!ready && (
            <div className="absolute inset-0 flex items-center justify-center text-whiteout/50">
              <FaSpinner className="animate-spin text-2xl" />
            </div>
          )}
        </div>

        <div className="flex justify-center px-5 py-4">
          <button
            type="button"
            onClick={snap}
            disabled={!ready}
            className="flex h-14 w-14 items-center justify-center rounded-full bg-whiteout ring-4 ring-white/25 transition-transform active:scale-95 disabled:opacity-40"
            aria-label={c.takePhoto}
          >
            <span className="h-11 w-11 rounded-full border-2 border-black/60 bg-whiteout" />
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default CameraCaptureModal
